import * as api from '@/api/theme-builder';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

export function useThemeBuilder(projectId?: string) {
    const queryClient = useQueryClient();
    const capabilities = useQuery({queryKey: ['theme-builder-capabilities'], queryFn: api.getThemeCapabilities});
    const project = useQuery({
        queryKey: ['theme-builder-project', projectId], queryFn: () => api.getThemeProject(projectId!), enabled: Boolean(projectId),
        refetchInterval: data => (data?.jobs?.some(job => ['queued', 'generating', 'validating'].includes(job.status)) ? 2000 : false)
    });
    const orders = useQuery({
        queryKey: ['theme-builder-dfy'], queryFn: api.getDfyOrders,
        refetchInterval: data => (data?.some(order => !['delivered', 'refunded', 'cancelled'].includes(order.status)) ? 5000 : false)
    });
    const refresh = () => Promise.all([queryClient.invalidateQueries({queryKey: ['theme-builder-project', projectId]}), queryClient.invalidateQueries({queryKey: ['theme-builder-projects']})]);
    return {
        capabilities, project, orders,
        create: useMutation({mutationFn: api.createThemeProject, onSuccess: async (created) => {
            queryClient.setQueryData(['theme-builder-project', created.id], created);
            await queryClient.invalidateQueries({queryKey: ['theme-builder-projects']});
        }}),
        prepareSetup: useMutation({mutationFn: api.prepareThemeSetup}),
        rename: useMutation({mutationFn: (name: string) => api.renameThemeProject(projectId!, name), onSuccess: refresh}),
        generate: useMutation({mutationFn: () => api.generateTheme(project.data!.id), onSuccess: refresh}),
        refine: useMutation({mutationFn: (message: string) => api.refineTheme(project.data!.id, message), onSuccess: refresh}),
        updateDraft: useMutation({mutationFn: (recipe: api.ThemeRecipe) => api.updateThemeDraft(project.data!.id, project.data!.draft_revision, recipe), onSuccess: refresh}),
        saveVersion: useMutation({mutationFn: () => api.saveThemeVersion(project.data!.id), onSuccess: refresh}),
        preview: useMutation({mutationFn: api.createThemePreview}),
        publish: useMutation({mutationFn: api.publishThemeVersion, onSuccess: async () => {
            await Promise.all([refresh(), queryClient.invalidateQueries({queryKey: ['marketplace_themes']})]);
        }}),
        cancel: useMutation({mutationFn: api.cancelThemeJob, onSuccess: refresh}),
        dfy: useMutation({mutationFn: api.startDfyCheckout})
    };
}
